/**
 * LDOS ecosystem Supabase auth configuration — R-029
 * (ldos-standards `06-SECURITY-KEYS.md` §"Session persistence & ecosystem SSO").
 *
 * SHARED MODULE. This file is a byte-identical copy in every LDOS app that
 * constructs its own Supabase browser client. The repos do not share an npm
 * package, so the file is duplicated verbatim:
 *   - Deeply-Smart-V2                     src/lib/ldosAuthConfig.ts
 *   - deeplysmart-plugin-command-center   src/lib/ldosAuthConfig.ts
 *   - deeplysmart-plugin-business-dna     src/lib/ldosAuthConfig.ts
 * Edit it in one place, then copy it to the others. Never fork the contents.
 *
 * WHAT IT FIXES (R-029 names three independent causes; this module covers two):
 *   1. Per-ORIGIN storage. supabase-js persists the session in localStorage,
 *      which is scoped to the origin, so apps on different hosts can never see
 *      each other's session. The cookie adapter below writes the session to a
 *      cookie scoped to a configurable parent domain instead, which is the only
 *      mechanism that makes one sign-in visible on app./dna./os./studio.
 *   3. Undeclared client config. `createClient(url, key)` with no auth options
 *      relies on implicit defaults and cannot share a session. Every option is
 *      declared explicitly here.
 * (Cause 2 — transient network errors treated as sign-out — is app-level and
 * lives in each app's auth context, not here.)
 *
 * GRACEFUL DEGRADATION — READ THIS BEFORE CHANGING ANYTHING.
 * The one-root-domain / subdomain move has NOT happened yet. When
 * VITE_AUTH_COOKIE_DOMAIN is unset (local dev and every deploy as of today)
 * this module falls back to localStorage and changes nothing observable: same
 * storage medium, same behaviour, only a different key. Cookie mode switches on
 * the day that env var is set, and needs no code change to do it.
 *
 * NO-SIGN-OUT GUARANTEE. Moving to the shared `storageKey` would orphan every
 * session supabase-js previously wrote under `sb-<ref>-auth-token`, signing
 * everyone out on deploy. `migrateLegacyAuthSession()` copies that value to the
 * shared key on first load, so existing sessions survive. Call it BEFORE
 * `createClient`.
 *
 * SECURITY NOTES.
 *   - The cookie carries the same session supabase-js already stores in
 *     localStorage; it is not a new secret. It is written `Secure` on https,
 *     `SameSite=Lax`, `Path=/`, with a Max-Age matching R-029's 90-day absolute
 *     session cap and rewritten on every token refresh.
 *   - It is NOT `HttpOnly` — it cannot be, because supabase-js reads it from
 *     JavaScript. Server-set HttpOnly cookies require the @supabase/ssr
 *     server-side pattern, which is a different (larger) piece of work.
 *   - Cookies are sent on every same-domain request, so keep an eye on header
 *     size: the adapter chunks values that exceed the ~4 KB per-cookie ceiling.
 */

/** The ONE storage key shared by every LDOS app. Do not localise or prefix. */
export const LDOS_AUTH_STORAGE_KEY = "ldos-auth";

/** Name of the env var holding the registrable parent domain (documentation). */
export const LDOS_AUTH_COOKIE_DOMAIN_ENV = "VITE_AUTH_COOKIE_DOMAIN";

/** 90 days — R-029's absolute session cap. Rewritten on every token refresh. */
const COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;

/** Browsers cap a single cookie near 4096 bytes including name + attributes. */
const COOKIE_CHUNK_SIZE = 3000;

/** Hard stop so a corrupt cookie jar can never spin a loop. */
const MAX_COOKIE_CHUNKS = 24;

/**
 * Structural match for supabase-js `SupportedStorage`. Declared locally rather
 * than imported so this file stays identical across repos on different
 * supabase-js versions (2.29 → 2.111 all accept this shape).
 */
export type LdosAuthStorage = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
};

/** The auth block handed to `createClient(url, key, options)`. */
export type LdosAuthClientOptions = {
  auth: {
    persistSession: true;
    autoRefreshToken: true;
    detectSessionInUrl: true;
    flowType: "pkce";
    storageKey: string;
    storage: LdosAuthStorage;
  };
};

/* ------------------------------------------------------------------ env --- */

/**
 * Reads VITE_AUTH_COOKIE_DOMAIN and normalises it to a leading-dot parent
 * domain (`.example.com`). Returns undefined — meaning "stay on localStorage" —
 * when unset, blank, or not a real parent domain (a single label such as
 * `localhost` cannot scope a shared cookie and is rejected rather than
 * silently producing a cookie the browser drops).
 *
 * The env var is read via a STATIC property access so Vite/esbuild can inline
 * it at build time; a dynamic lookup would not be replaced.
 */
export function getLdosAuthCookieDomain(): string | undefined {
  let raw: unknown;
  try {
    raw = import.meta.env.VITE_AUTH_COOKIE_DOMAIN;
  } catch {
    raw = undefined;
  }
  if (typeof raw !== "string") return undefined;
  const trimmed = raw.trim();
  if (trimmed === "" || trimmed === "undefined" || trimmed === "null") return undefined;
  const bare = trimmed.charAt(0) === "." ? trimmed.slice(1) : trimmed;
  if (bare === "" || bare.indexOf(".") === -1) return undefined;
  return "." + bare;
}

/* --------------------------------------------------------------- cookies --- */

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined" || !document.cookie) return null;
  const prefix = name + "=";
  const parts = document.cookie.split(";");
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i].trim();
    if (part.indexOf(prefix) === 0) return part.slice(prefix.length);
  }
  return null;
}

function writeCookie(name: string, value: string, domain: string): void {
  if (typeof document === "undefined") return;
  const isHttps = typeof location !== "undefined" && location.protocol === "https:";
  const secure = isHttps ? "; Secure" : "";
  document.cookie =
    name +
    "=" +
    value +
    "; Domain=" +
    domain +
    "; Path=/; Max-Age=" +
    String(COOKIE_MAX_AGE_SECONDS) +
    "; SameSite=Lax" +
    secure;
}

function deleteCookie(name: string, domain: string): void {
  if (typeof document === "undefined") return;
  document.cookie = name + "=; Domain=" + domain + "; Path=/; Max-Age=0; SameSite=Lax";
}

/**
 * Cookie-backed storage scoped to `domain`, so a session written by one
 * subdomain is readable by all the others. Values longer than a single cookie
 * are split across `<key>.0`, `<key>.1`, … the same way @supabase/ssr does it.
 */
function createCookieStorage(domain: string): LdosAuthStorage {
  const clearChunksFrom = (key: string, start: number): void => {
    for (let i = start; i < MAX_COOKIE_CHUNKS; i++) {
      const name = key + "." + String(i);
      if (readCookie(name) === null) break;
      deleteCookie(name, domain);
    }
  };

  return {
    getItem(key: string): string | null {
      const single = readCookie(key);
      if (single !== null) return safeDecode(single);
      let joined = "";
      let found = false;
      for (let i = 0; i < MAX_COOKIE_CHUNKS; i++) {
        const chunk = readCookie(key + "." + String(i));
        if (chunk === null) break;
        joined += chunk;
        found = true;
      }
      return found ? safeDecode(joined) : null;
    },
    setItem(key: string, value: string): void {
      const encoded = encodeURIComponent(value);
      if (encoded.length <= COOKIE_CHUNK_SIZE) {
        writeCookie(key, encoded, domain);
        clearChunksFrom(key, 0);
        return;
      }
      // Chunked representation replaces the single-cookie one.
      deleteCookie(key, domain);
      let index = 0;
      for (let offset = 0; offset < encoded.length; offset += COOKIE_CHUNK_SIZE) {
        if (index >= MAX_COOKIE_CHUNKS) break;
        writeCookie(key + "." + String(index), encoded.slice(offset, offset + COOKIE_CHUNK_SIZE), domain);
        index++;
      }
      clearChunksFrom(key, index);
    },
    removeItem(key: string): void {
      deleteCookie(key, domain);
      clearChunksFrom(key, 0);
    },
  };
}

/* ---------------------------------------------------------- localStorage --- */

function createLocalStorageAdapter(): LdosAuthStorage | null {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const probe = "__ldos_auth_probe__";
    window.localStorage.setItem(probe, "1");
    window.localStorage.removeItem(probe);
    return {
      getItem: (key: string) => window.localStorage.getItem(key),
      setItem: (key: string, value: string) => window.localStorage.setItem(key, value),
      removeItem: (key: string) => window.localStorage.removeItem(key),
    };
  } catch {
    // Private mode / blocked storage: fall through to memory.
    return null;
  }
}

function createMemoryStorage(): LdosAuthStorage {
  const map: Record<string, string> = {};
  return {
    getItem: (key: string) => (Object.prototype.hasOwnProperty.call(map, key) ? map[key] : null),
    setItem: (key: string, value: string) => {
      map[key] = value;
    },
    removeItem: (key: string) => {
      delete map[key];
    },
  };
}

/* ----------------------------------------------------------- the adapter --- */

let storageInstance: LdosAuthStorage | null = null;

/**
 * The single storage adapter this app uses for auth. Cookie-backed when
 * VITE_AUTH_COOKIE_DOMAIN is set, otherwise localStorage (memory as a last
 * resort). Memoised so the migration helper and the client agree.
 */
export function getLdosAuthStorage(): LdosAuthStorage {
  if (storageInstance) return storageInstance;
  const domain = getLdosAuthCookieDomain();
  if (domain) {
    storageInstance = createCookieStorage(domain);
  } else {
    storageInstance = createLocalStorageAdapter() ?? createMemoryStorage();
  }
  return storageInstance;
}

/** True when the session is stored in a parent-domain cookie (SSO active). */
export function isLdosCookieSessionEnabled(): boolean {
  return getLdosAuthCookieDomain() !== undefined;
}

/* ----------------------------------------------------------- migration ----- */

/** `https://<ref>.supabase.co` → `<ref>`. */
function projectRefFromUrl(supabaseUrl: string): string | null {
  try {
    const host = new URL(supabaseUrl).hostname;
    const ref = host.split(".")[0];
    return ref || null;
  } catch {
    return null;
  }
}

/**
 * Carries an existing supabase-js session across the storage-key rename so the
 * switch to `LDOS_AUTH_STORAGE_KEY` does not sign everybody out.
 *
 * MUST be called before `createClient`. Best-effort and never throws: a failed
 * migration costs one re-login, an exception here would cost the whole app.
 * The legacy entry is removed once copied so a live session token is not left
 * lying in a second location.
 */
export function migrateLegacyAuthSession(supabaseUrl: string): void {
  try {
    const storage = getLdosAuthStorage();
    if (storage.getItem(LDOS_AUTH_STORAGE_KEY) !== null) return;
    if (typeof window === "undefined" || !window.localStorage) return;
    const ref = projectRefFromUrl(supabaseUrl);
    if (!ref) return;
    const legacyKey = "sb-" + ref + "-auth-token";
    const legacy = window.localStorage.getItem(legacyKey);
    if (!legacy) return;
    storage.setItem(LDOS_AUTH_STORAGE_KEY, legacy);
    window.localStorage.removeItem(legacyKey);
  } catch {
    // Best effort only.
  }
}

/* -------------------------------------------------------------- options ---- */

/**
 * The auth options every LDOS Supabase browser client passes to createClient.
 * Nothing here is optional or implied — that is the point (R-029 cause 3).
 */
export function ldosAuthClientOptions(): LdosAuthClientOptions {
  return {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: "pkce",
      storageKey: LDOS_AUTH_STORAGE_KEY,
      storage: getLdosAuthStorage(),
    },
  };
}

/**
 * Raw persisted session string, or null. Lets an app read the stored session
 * without knowing whether it lives in a cookie or localStorage — used as the
 * fallback when `getSession()` fails transiently (R-029 cause 2).
 */
export function readPersistedAuthRecord(): string | null {
  try {
    return getLdosAuthStorage().getItem(LDOS_AUTH_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Drops the shared session from storage. Part of R-029's "sign-out is global":
 * clearing the parent-domain cookie ends the session for every app on the
 * domain, not just this one. Call alongside `supabase.auth.signOut()`.
 */
export function clearLdosAuthSession(): void {
  try {
    getLdosAuthStorage().removeItem(LDOS_AUTH_STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
}
